import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, MessageCircle, LogIn, LogOut, ChevronDown } from "lucide-react";
import { useLanguage, pick, waLink, LANGS } from "@/i18n";
import { useAuth } from "@/auth";
import { api } from "@/api";

const DEST_ORDER = ["naples", "pozzuoli", "bacoli", "sorrento", "pompeii", "capri", "procida", "ischia", "amalfi-coast", "caserta", "apennines"];

export default function Navbar() {
  const { lang, setLang, t } = useLanguage();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [expOpen, setExpOpen] = useState(false);
  const [mobileExpOpen, setMobileExpOpen] = useState(false);
  const [dests, setDests] = useState([]);
  const [exps, setExps] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.destinations().then(setDests).catch(() => {});
    api.experiences().then(setExps).catch(() => {});
  }, []);

  const groups = DEST_ORDER
    .map((s) => ({ dest: dests.find((d) => d.slug === s), items: exps.filter((e) => e.destination === s) }))
    .filter((g) => g.dest && g.items.length > 0);

  const links = [
    { to: "/boat", label: t("nav_boat"), id: "nav-boat" },
    { to: "/taste", label: t("nav_taste"), id: "nav-taste" },
    { to: "/stay", label: t("nav_stay"), id: "nav-stay" },
    { to: "/companion", label: t("nav_companion"), id: "nav-companion" },
    { to: "/create", label: t("nav_create"), id: "nav-create" },
  ];

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-ivory/80 border-b border-stone2/40" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-6">
        <Link to="/" className="flex flex-col leading-none" data-testid="logo-link">
          <span className="font-serif text-2xl tracking-tight text-ink">Flegrea</span>
          <span className="text-[10px] font-medium uppercase tracking-[0.35em] text-medblue">Experience</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {/* EXPERIENCES MEGA MENU */}
          <div className="relative" onMouseEnter={() => setExpOpen(true)} onMouseLeave={() => setExpOpen(false)}>
            <button
              type="button"
              data-testid="nav-experiences"
              aria-expanded={expOpen}
              aria-haspopup="true"
              onClick={() => {
                if (expOpen) {
                  setExpOpen(false);
                  navigate("/experiences");
                } else {
                  setExpOpen(true);
                }
              }}
              onFocus={() => setExpOpen(true)}
              className={`inline-flex items-center gap-1.5 text-sm font-medium transition-colors duration-300 ${expOpen ? "text-medblue" : "text-ink/70 hover:text-medblue"}`}
            >
              {t("nav_experiences")}
              <ChevronDown size={14} strokeWidth={1.5} className={`transition-transform duration-300 ${expOpen ? "rotate-180" : ""}`} />
            </button>
            {expOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-5 w-[900px] max-w-[92vw]" data-testid="nav-mega-menu">
                <div className="bg-ivory border border-stone2/50 shadow-2xl shadow-ink/10 p-8 grid grid-cols-4 gap-x-8 gap-y-7 max-h-[70vh] overflow-y-auto">
                  <Link
                    to="/experiences"
                    data-testid="nav-mega-all"
                    onClick={() => setExpOpen(false)}
                    className="col-span-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-medblue hover:text-ink border-b border-stone2/40 pb-4 transition-colors duration-300"
                  >
                    {t("nav_all_experiences")}
                  </Link>
                  {groups.map((g) => (
                    <div key={g.dest.slug} data-testid={`nav-mega-group-${g.dest.slug}`}>
                      <Link
                        to={`/experiences?destination=${g.dest.slug}`}
                        data-testid={`nav-mega-dest-${g.dest.slug}`}
                        onClick={() => setExpOpen(false)}
                        className="font-serif text-lg leading-tight text-ink hover:text-medblue transition-colors duration-300"
                      >
                        {pick(g.dest.name, lang)}
                      </Link>
                      <ul className="mt-2.5 space-y-1.5">
                        {g.items.map((e) => (
                          <li key={e.slug}>
                            <Link
                              to={`/experiences/${e.slug}`}
                              data-testid={`nav-mega-exp-${e.slug}`}
                              onClick={() => setExpOpen(false)}
                              className="block text-xs text-ink/60 hover:text-medblue leading-snug transition-colors duration-300"
                            >
                              {pick(e.title, lang)}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  <div className="col-span-4 border-t border-stone2/40 pt-4 flex justify-end">
                    <Link
                      to="/gallery"
                      data-testid="nav-mega-gallery"
                      onClick={() => setExpOpen(false)}
                      className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-terracotta hover:text-ink transition-colors duration-300"
                    >
                      {t("nav_gallery")} →
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className="text-sm font-medium text-ink/70 hover:text-medblue transition-colors duration-300"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          {user ? (
            <div className="hidden sm:flex items-center gap-3" data-testid="user-menu">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover border border-stone2" data-testid="user-avatar" />
              ) : (
                <span className="w-8 h-8 rounded-full bg-medblue text-ivory flex items-center justify-center text-xs font-semibold" data-testid="user-avatar">
                  {(user.name || user.email || "?")[0].toUpperCase()}
                </span>
              )}
              <Link to="/account" data-testid="user-name" className="text-xs text-ink/60 max-w-[120px] truncate hover:text-medblue transition-colors duration-300">{user.name || user.email}</Link>
              <button
                onClick={logout}
                data-testid="logout-button"
                className="text-ink/40 hover:text-ink transition-colors duration-300"
                aria-label={t("sign_out")}
                title={t("sign_out")}
              >
                <LogOut size={16} strokeWidth={1.5} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate("/login")}
              data-testid="sign-in-button"
              className="hidden sm:inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.15em] text-ink/60 hover:text-medblue transition-colors duration-300"
            >
              <LogIn size={14} strokeWidth={1.5} /> {t("sign_in")}
            </button>
          )}
          <div className="hidden sm:flex items-center gap-2" data-testid="language-switcher">
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-${l.code}`}
                onClick={() => setLang(l.code)}
                aria-label={l.label}
                title={l.label}
                className={`transition-all duration-300 ${lang === l.code ? "opacity-100 ring-1 ring-medblue ring-offset-1" : "opacity-45 hover:opacity-90"}`}
              >
                <img src={l.flag} alt={l.label} className="w-5 h-[15px] object-cover rounded-[1px]" />
              </button>
            ))}
          </div>
          <a
            href={waLink(t("wa_message") + " ")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-book-button"
            className="hidden md:inline-flex items-center gap-2 bg-medblue text-ivory text-xs font-medium uppercase tracking-[0.15em] px-5 py-3 hover:bg-ink transition-colors duration-300"
          >
            <MessageCircle size={14} strokeWidth={1.5} />
            {t("nav_book")}
          </a>
          <button
            className="lg:hidden text-ink"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-stone2/40 bg-ivory max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {/* EXPERIENCES ACCORDION */}
            <div>
              <button
                type="button"
                data-testid="mobile-nav-experiences"
                aria-expanded={mobileExpOpen}
                onClick={() => setMobileExpOpen((o) => !o)}
                className="w-full flex items-center justify-between text-left font-serif text-2xl text-ink hover:text-medblue transition-colors duration-300"
              >
                {t("nav_experiences")}
                <ChevronDown size={18} strokeWidth={1.5} className={`transition-transform duration-300 ${mobileExpOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileExpOpen && (
                <div className="mt-4 pl-4 border-l border-stone2/50 space-y-4" data-testid="mobile-exp-panel">
                  <button
                    data-testid="mobile-nav-all-experiences"
                    onClick={() => { setOpen(false); navigate("/experiences"); }}
                    className="block text-[11px] uppercase tracking-[0.2em] text-medblue"
                  >
                    {t("nav_all_experiences")}
                  </button>
                  {groups.map((g) => (
                    <div key={g.dest.slug}>
                      <button
                        data-testid={`mobile-nav-dest-${g.dest.slug}`}
                        onClick={() => { setOpen(false); navigate(`/experiences?destination=${g.dest.slug}`); }}
                        className="text-left font-serif text-lg text-ink hover:text-medblue transition-colors duration-300"
                      >
                        {pick(g.dest.name, lang)}
                      </button>
                      <div className="mt-1.5 flex flex-col gap-1.5 pl-3">
                        {g.items.map((e) => (
                          <button
                            key={e.slug}
                            data-testid={`mobile-nav-exp-${e.slug}`}
                            onClick={() => { setOpen(false); navigate(`/experiences/${e.slug}`); }}
                            className="text-left text-sm text-ink/55 hover:text-medblue leading-snug transition-colors duration-300"
                          >
                            {pick(e.title, lang)}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <button
                    data-testid="mobile-nav-gallery"
                    onClick={() => { setOpen(false); navigate("/gallery"); }}
                    className="block text-[11px] uppercase tracking-[0.2em] text-terracotta pt-2"
                  >
                    {t("nav_gallery")} →
                  </button>
                </div>
              )}
            </div>
            {links.map((l) => (
              <button
                key={l.to}
                data-testid={`mobile-${l.id}`}
                onClick={() => {
                  setOpen(false);
                  navigate(l.to);
                }}
                className="text-left font-serif text-2xl text-ink hover:text-medblue transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
            <div className="flex items-center gap-3 pt-4 border-t border-stone2/40">
              {user ? (
                <>
                  <span className="text-sm text-ink/70 flex-1" data-testid="mobile-user-name">{user.name || user.email}</span>
                  <button data-testid="mobile-account-link" onClick={() => { navigate("/account"); setOpen(false); }} className="text-sm text-medblue">{t("account_title")}</button>
                  <button data-testid="mobile-logout-button" onClick={() => { logout(); setOpen(false); }} className="text-sm text-ink/50">{t("sign_out")}</button>
                </>
              ) : (
                <button data-testid="mobile-sign-in-button" onClick={() => { navigate("/login"); setOpen(false); }} className="inline-flex items-center gap-2 text-sm text-medblue">
                  <LogIn size={15} strokeWidth={1.5} /> {t("sign_in")}
                </button>
              )}
            </div>
            <div className="flex items-center gap-3 pt-4 border-t border-stone2/40">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`mobile-lang-${l.code}`}
                  onClick={() => setLang(l.code)}
                  aria-label={l.label}
                  className={`transition-all duration-300 ${lang === l.code ? "opacity-100 ring-1 ring-medblue" : "opacity-45"}`}
                >
                  <img src={l.flag} alt={l.label} className="w-6 h-[18px] object-cover rounded-[1px]" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
